import React, { useEffect, useState } from 'react';
import { X, Shield } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { fetchRoles } from '../../services/api/adminRbacRoles';
import AddMembershipModal from './AddMembershipModal';

const UserDetailDrawer = ({ open, userId, onClose, onChanged }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [memberships, setMemberships] = useState([]);
  const [removed, setRemoved] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);

  // Fetch the live RbacRole list once on open. Excludes "owner" (admin can't
  // assign owner via this UI) but includes custom roles.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchRoles()
      .then((res) => {
        if (cancelled) return;
        // Backend returns { roles: [...] }
        const rows = res.data?.roles || res.data?.data || (Array.isArray(res.data) ? res.data : []);
        const codes = rows.map((r) => r.code).filter((c) => c && c !== 'owner');
        setRoleOptions(codes.length ? codes : ['admin', 'member', 'viewer']);
      })
      .catch(() => {
        if (!cancelled) setRoleOptions(['admin', 'member', 'viewer']);
      });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true); setError(null); setSuccess(null);
        const res = await adminAPI.getUser(userId);
        if (cancelled) return;
        const u = res.data;
        setUser(u);
        setName(u.name || '');
        setEmail(u.email || '');
        setMemberships(u.memberships || []);
        setRemoved(new Set());
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, userId]);

  const setMembershipRole = (clientId, role) => {
    setMemberships((prev) => prev.map((m) => m.client_id === clientId ? { ...m, role } : m));
  };
  const removeMembership = (clientId) => {
    setRemoved((prev) => { const next = new Set(prev); next.add(clientId); return next; });
  };
  const undoRemove = (clientId) => {
    setRemoved((prev) => { const next = new Set(prev); next.delete(clientId); return next; });
  };
  const addMembership = ({ client_id, client_name, role }) => {
    setMemberships((prev) => [...prev, { client_id, client_name, role, is_owner: false, _new: true }]);
  };

  const handleSave = async () => {
    try {
      setSaving(true); setError(null); setSuccess(null);

      if (name !== user.name || email !== user.email) {
        await adminAPI.updateUser(user.id, { name, email });
      }

      const finalMemberships = memberships
        .filter((m) => !removed.has(m.client_id))
        .map((m) => ({ client_id: m.client_id, role: m.role }));
      await adminAPI.updateMemberships(user.id, finalMemberships);

      setSuccess('Saved.');
      onChanged && onChanged();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    try {
      setDeleting(true); setError(null);
      await adminAPI.deleteUser(user.id);
      onChanged && onChanged();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  const ownsAny = (memberships || []).some((m) => m.is_owner);
  const isInternal = user?.internal_admin;
  const deleteDisabled = ownsAny || isInternal;
  const deleteTooltip = isInternal
    ? 'Internal admin — cannot be deleted'
    : ownsAny
      ? `Owner of ${user?.owned_workspaces?.length || 0} workspace(s) — delete those first`
      : '';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-full max-w-xl bg-card shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              {user?.email || 'Loading...'}
              {isInternal && <span title="Internal admin"><Shield className="h-4 w-4 text-blue-600" /></span>}
            </h3>
            <p className="text-xs text-muted-foreground">User · joined {user?.created_at?.slice(0, 10)}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading && <div className="text-sm text-muted-foreground">Loading...</div>}
          {error && <div className="mb-3 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
          {success && <div className="mb-3 p-3 bg-green-50 text-green-700 text-sm rounded-lg">{success}</div>}

          {user && (
            <>
              <div className="space-y-3 mb-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                         className="w-full text-sm border border-input rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                         className="w-full text-sm border border-input rounded-lg px-3 py-2" />
                </div>
                <div className="text-xs text-muted-foreground">
                  Provider: {user.provider || 'email'}
                </div>
              </div>

              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Workspace memberships</h4>
              <ul className="divide-y divide-gray-100 border border-border rounded-lg mb-3">
                {memberships.length === 0 && (
                  <li className="px-3 py-2 text-sm text-muted-foreground">No workspaces.</li>
                )}
                {memberships.map((m) => {
                  const isRemoved = removed.has(m.client_id);
                  return (
                    <li key={m.client_id} className={`px-3 py-2 flex items-center gap-2 ${isRemoved ? 'opacity-50 line-through' : ''}`}>
                      <span className="text-sm text-foreground flex-1">{m.client_name}</span>
                      <select
                        value={m.role}
                        disabled={m.is_owner || isRemoved}
                        onChange={(e) => setMembershipRole(m.client_id, e.target.value)}
                        className="text-sm border border-input rounded px-2 py-1 disabled:bg-muted/50"
                      >
                        {m.is_owner ? (
                          <option value="owner">owner</option>
                        ) : roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      {m.is_owner ? (
                        <span className="text-xs text-muted-foreground px-2">(locked)</span>
                      ) : isRemoved ? (
                        <button type="button" onClick={() => undoRemove(m.client_id)}
                                className="text-xs text-blue-600 hover:underline">Undo</button>
                      ) : (
                        <button type="button" onClick={() => removeMembership(m.client_id)}
                                className="text-xs text-red-600 hover:underline">Remove</button>
                      )}
                    </li>
                  );
                })}
              </ul>

              <button type="button" onClick={() => setAddOpen(true)}
                      className="text-sm text-foreground border border-input rounded-lg px-3 py-1.5 hover:bg-muted/50">
                + Add workspace
              </button>

              <div className="mt-8 pt-4 border-t border-border">
                <h4 className="text-xs font-semibold text-red-600 uppercase mb-2">Danger zone</h4>
                <button
                  type="button"
                  disabled={deleteDisabled || deleting}
                  onClick={handleDelete}
                  title={deleteTooltip}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Deleting...' : 'Delete user'}
                </button>
                {deleteDisabled && <p className="mt-1 text-xs text-muted-foreground">{deleteTooltip}</p>}
              </div>
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border flex justify-end gap-2">
          <button onClick={onClose}
                  className="px-4 py-2 text-sm text-foreground border border-input rounded-lg hover:bg-muted/50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>

        <AddMembershipModal
          open={addOpen}
          existingClientIds={memberships.filter((m) => !removed.has(m.client_id)).map((m) => m.client_id)}
          onClose={() => setAddOpen(false)}
          onPick={addMembership}
        />
      </div>
    </div>
  );
};

export default UserDetailDrawer;
